import type { CellContext, ColumnDef } from "@tanstack/react-table";

import {
  renderApyTooltip,
  renderCirculatingSupplyTableCell,
  renderCirculatingSupplyTooltip,
  renderCoinUserTableCell,
  renderHoldersCell,
  renderMarketCapTableCell,
  renderMarketCapTooltip,
  renderPriceUsdTableCell,
  renderVolumeTableCell,
} from "@/components/app/trade/trending-coins/trending-coins-cell-content";
import CoinPriceChangePercentageFormatter from "@/components/shared/coin-price-change-percentage-formatter";
import type { DaoCoinStat } from "@/graphql/codegen/graphql";
import { basisPointsToPercent } from "@/utils/currency";
import { isWrappedAsset } from "@/utils/helpers";

import TradeCoinActions from "../actions/trade-coin-actions";
import { renderSortingHeader } from "../table-sorting-headers";
import type { TrendingTabSelection } from "./constants";

interface CustomCellContext extends CellContext<DaoCoinStat, unknown> {
  focusExchangeRate: number;
  desoExchangeRate: number;
  pageOffset: number;
  currentUserPublicKey: string;
  refetchData: () => void;
  onViewHoldersClick?: (coinPublicKey: string) => void;
}

export const columns = ({
  selectedTab,
}: {
  selectedTab: TrendingTabSelection;
}): ColumnDef<DaoCoinStat>[] => {
  return [
    {
      accessorKey: "id",
      header: () => <span className="text-xxs text-muted">#</span>,
      cell: (props) => {
        const {
          row: { id },
          pageOffset,
        } = props as CustomCellContext;
        return (
          <span className="pl-1 font-mono text-xs text-muted">
            {+id + pageOffset + 1}
          </span>
        );
      },
    },
    {
      accessorKey: "coinPublicKey",
      header: () => <span className="text-xxs">Token & Category</span>,
      cell: (props) => {
        const { row, currentUserPublicKey } = props as CustomCellContext;
        const { coinUsername, coinPublicKey, coinCategory } = row.original;

        return renderCoinUserTableCell({
          coinUsername: coinUsername ?? "",
          coinPublicKey: coinPublicKey ?? "",
          coinCategory: coinCategory ?? "",
          clickable: true,
          isTokenCreator: coinPublicKey === currentUserPublicKey,
        });
      },
    },
    {
      accessorKey: "priceUsd",
      header: ({ column }) =>
        renderSortingHeader({ column, title: "Price", className: "text-xxs" }),
      cell: (props) => {
        const { row, focusExchangeRate, desoExchangeRate } =
          props as CustomCellContext;
        return renderPriceUsdTableCell(
          row.original.coinPublicKey,
          row.getValue("priceUsd"),
          focusExchangeRate,
          desoExchangeRate,
        );
      },
    },
    {
      accessorKey: "marketCapUsd",
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: "Market Cap",
          infoTooltip: renderMarketCapTooltip(),
          className: "text-xxs",
        }),
      cell: (props) => {
        const { row, focusExchangeRate, desoExchangeRate } =
          props as CustomCellContext;

        if (isWrappedAsset(row.original.coinPublicKey)) {
          return <div className="font-mono text-xs">N/A</div>;
        }

        return renderMarketCapTableCell({
          publicKey: row.original.coinPublicKey,
          marketCapUsd: row.original.marketCapUsd ?? 0,
          focusExchangeRate,
          desoExchangeRate,
        });
      },
    },
    {
      accessorKey: selectedTab.volumeAccessorKey,
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: `Vol (${selectedTab.timeDuration})`,
          className: "text-xxs",
        }),
      cell: (props) => {
        const { row, focusExchangeRate, desoExchangeRate } =
          props as CustomCellContext;

        return renderVolumeTableCell({
          publicKey: row.original.coinPublicKey,
          volumeUsd: row.getValue(selectedTab.volumeAccessorKey),
          focusExchangeRate,
          desoExchangeRate,
        });
      },
    },
    {
      accessorKey: selectedTab.priceAccessorKey,
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: selectedTab.timeDuration,
          className: "text-xxs",
        }),
      cell: ({ row }) => (
        <div className="relative flex items-center">
          <CoinPriceChangePercentageFormatter
            arrowSize={12}
            price={Number(row.getValue(selectedTab.priceAccessorKey))}
          />
        </div>
      ),
    },
    {
      accessorKey: "circulatingSupply",
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: "Circ. Supply",
          infoTooltip: renderCirculatingSupplyTooltip(),
          className: "text-xxs",
        }),
      cell: ({ row }) => {
        if (isWrappedAsset(row.original.coinPublicKey)) {
          return <div className="font-mono text-xs">N/A</div>;
        }
        return renderCirculatingSupplyTableCell({
          circulatingSupply: row.getValue("circulatingSupply"),
          totalSupply: Number(row.original.totalSupply ?? "0"),
        });
      },
    },
    {
      accessorKey: "apyBasisPoints",
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: "APY",
          infoTooltip: renderApyTooltip(),
          className: "text-xxs",
        }),
      cell: ({ row }) => (
        <div className="flex flex-row gap-0 pl-1 font-mono text-xs">
          <CoinPriceChangePercentageFormatter
            price={basisPointsToPercent(Number(row.getValue("apyBasisPoints")))}
            showArrow={false}
          />
        </div>
      ),
    },
    {
      accessorKey: "numHolders",
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: "Holders",
          className: "text-xxs",
        }),
      cell: ({ row }) =>
        renderHoldersCell({
          row: {
            ...row,
            original: {
              ...row.original,
              coinUsername: row.original.coinUsername ?? undefined,
            },
          },
        }),
    },
    {
      id: "actions",
      cell: (props) => {
        const { row, refetchData } = props as CustomCellContext;
        return (
          <TradeCoinActions
            coinUsername={row.original.coinUsername ?? ""}
            coinPublicKey={row.original.coinPublicKey ?? ""}
            hasAmm={row.original.tokenAmmCreatedAt !== null}
            refetchData={refetchData}
            hideWalletActions={true}
          />
        );
      },
    },
  ];
};
